/**
 * Entry point of the React application.
 * 
 * Uses modern Web3 stack:
 * - Wagmi v2 + Viem v2
 * - RainbowKit v2
 * - React Query v5
 * - SIWE (Sign-In with Ethereum)
 */

// Import React and ReactDOM for rendering
import React from "react";
import ReactDOM from "react-dom/client";

// Import the main App component and styles
import App from "./App";
import { BrowserRouter } from "react-router-dom";
import ScrollToTop from "./utils/ScrollToTop";

// Import styles
import "./styles/index.css";
import "react-toastify/dist/ReactToastify.css";
import "@rainbow-me/rainbowkit/styles.css";

// Import time-ago library and its English locale
import TimeAgo from "javascript-time-ago";
import en from "javascript-time-ago/locale/en.json";

// Import polyfills for better compatibility
import "./polyfills";

// Import Wagmi v2 and RainbowKit v2
import { WagmiProvider } from "wagmi";
import { RainbowKitProvider, lightTheme } from "@rainbow-me/rainbowkit";
import { wagmiConfig } from "./config/wagmi";

// Import React Query for data fetching
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

// Import Redux store
import { Provider } from "react-redux";
import { store } from "./redux/store/store";

import { HelmetProvider } from "react-helmet-async";
import ErrorBoundary from "./components/common/ErrorBoundary";

// Set the default locale for time-ago library
TimeAgo.addDefaultLocale(en);

// Create a root for React rendering
const root = ReactDOM.createRoot(document.getElementById("root"));

// Create a React Query client with optimized defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 2, // 2 minutes
      retry: 1,
    },
  },
});

// Custom RainbowKit theme matching FunDeSci brand
const customTheme = lightTheme({
  accentColor: "#7c3aed", // Primary purple
  accentColorForeground: "white",
  borderRadius: "medium",
  fontStack: "system",
  overlayBlur: "small",
});

// Render the app with all the providers and components
root.render(
  <React.StrictMode>
    <HelmetProvider>
      <Provider store={store}>
        <BrowserRouter>
          <ErrorBoundary>
            <WagmiProvider config={wagmiConfig}>
              <QueryClientProvider client={queryClient}>
                <RainbowKitProvider
                  theme={customTheme}
                  modalSize="compact"
                  showRecentTransactions={true}
                >
                  <ScrollToTop />
                  <App />
                </RainbowKitProvider>
              </QueryClientProvider>
            </WagmiProvider>
          </ErrorBoundary>
        </BrowserRouter>
      </Provider>
    </HelmetProvider>
  </React.StrictMode>
);
